import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { FisherRegistration, FishingLicence, FishingZone, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const REGISTRAR_ROLES = ['SFA_LICENSING'];
const LICENCE_LIST_ROLES = ['SFA_LICENSING', 'QUOTA_MANAGER', 'EXPORT_CERT_OFFICER', 'OBSERVER_COORDINATOR'];
const GEAR_TYPES = ['HANDLINE', 'TROLLING', 'LONGLINE', 'GILLNET', 'FISH_TRAP', 'PURSE_SEINE', 'DIVING_SPEARGUN', 'POLE_AND_LINE'] as const;
const LICENCE_CLASSES = ['ARTISANAL', 'SEMI_INDUSTRIAL', 'INDUSTRIAL', 'SPORT'] as const;
const ZONE_KINDS = ['INSHORE', 'BANK', 'OFFSHORE_EEZ', 'RESTRICTED'] as const;

type Tab = 'fishers' | 'licences' | 'zones';

/** M32 Fisheries — fisher registration & fishing-licence workspace, plus the FishingZone reference catalogue licences apply against. */
export function FishersLicensing() {
  const [tab, setTab] = useState<Tab>('fishers');
  return (
    <div>
      <PageHeader title="Fishers & Licensing" subtitle="SFA fisher registration, fishing licences, and the fishing-zone catalogue (M32)." />
      <TabStrip
        tabs={[
          { key: 'fishers', label: 'Fisher Registrations' },
          { key: 'licences', label: 'Fishing Licences' },
          { key: 'zones', label: 'Fishing Zones' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'fishers' ? <FishersTab /> : tab === 'licences' ? <LicencesTab /> : <ZonesTab />}
    </div>
  );
}

// ── Fishers ────────────────────────────────────────────────────────────────

function FishersTab() {
  const { persona } = useAuth();
  const isRegistrar = persona ? persona.roles.some((r) => REGISTRAR_ROLES.includes(r)) : false;
  const isObserver = persona ? persona.roles.includes('OBSERVER_COORDINATOR') : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m32/fishers?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<FisherRegistration>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Fisher no, name, NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['PENDING_PAYMENT', 'ACTIVE', 'SUSPENDED', 'REVOKED', 'REJECTED', 'WITHDRAWN'].map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
        {isRegistrar ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Register fisher
          </button>
        ) : null}
      </div>

      {!isRegistrar && !isObserver ? <RoleNotice roles={['SFA_LICENSING', 'OBSERVER_COORDINATOR']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No fisher registrations match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Fisher No</th>
                <th className="table-head">Name</th>
                <th className="table-head">Home port</th>
                <th className="table-head">District</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((f) => (
                <tr key={f.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(f.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{f.fisherNo}</td>
                  <td className="table-cell">{f.fullName}</td>
                  <td className="table-cell text-xs text-slate-500">{f.homePort}</td>
                  <td className="table-cell text-xs text-slate-500">{f.homeDistrict}</td>
                  <td className="table-cell">
                    <StatusBadge status={f.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <FisherDetailModal
          id={selectedId}
          isRegistrar={isRegistrar}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
      {showCreate ? (
        <RegisterFisherModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function FisherDetailModal({ id, isRegistrar, onClose, onChanged }: { id: string; isRegistrar: boolean; onClose: () => void; onChanged: () => void }) {
  const fq = useApiQuery<FisherRegistration>(`/api/v1/m32/fishers/${id}`);
  const f = fq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [showReason, setShowReason] = useState<'suspend' | 'revoke' | null>(null);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const reinstate = () => call(() => api.post(`/api/v1/m32/fishers/${id}/reinstate`));
  const withdraw = () => call(() => api.post(`/api/v1/m32/fishers/${id}/withdraw`));
  const confirmReasoned = () => call(() => api.post(`/api/v1/m32/fishers/${id}/${showReason}`, { reason }));

  return (
    <Modal title={f ? `${f.fisherNo} — ${f.fullName}` : 'Fisher registration'} onClose={onClose} wide>
      {fq.error ? <ErrorNote message={fq.error} /> : null}
      {fq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {f ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={f.status} />} />
            <Detail label="NIN" value={f.nin} mono />
            <Detail label="Phone" value={f.phone ?? '—'} />
            <Detail label="Home district" value={f.homeDistrict} />
            <Detail label="Home port" value={f.homePort} />
            <Detail label="Date of birth" value={formatDate(f.dob)} />
            <Detail label="Fee intent ref" value={f.payIntentRefNo ?? '—'} mono />
            <Detail label="Fee paid" value={formatDateTime(f.feePaidAt)} />
            <Detail label="Card (DocVault)" value={f.cardDocVaultRef ?? '—'} mono />
          </div>
          {f.rejectionReason ? <ErrorNote message={`Rejected: ${f.rejectionReason}`} /> : null}
          {f.suspendedReason ? <ErrorNote message={`Suspended: ${f.suspendedReason}`} /> : null}
          {f.revokedReason ? <ErrorNote message={`Revoked: ${f.revokedReason}`} /> : null}

          {isRegistrar ? (
            <div className="border-t border-slate-100 pt-4">
              {!showReason ? (
                <div className="flex flex-wrap gap-2">
                  {f.status === 'ACTIVE' ? (
                    <button className="btn-secondary" disabled={busy} onClick={() => setShowReason('suspend')}>
                      Suspend
                    </button>
                  ) : null}
                  {f.status === 'SUSPENDED' ? (
                    <button className="btn-success" disabled={busy} onClick={reinstate}>
                      Reinstate
                    </button>
                  ) : null}
                  {f.status === 'ACTIVE' || f.status === 'SUSPENDED' ? (
                    <button className="btn-danger" disabled={busy} onClick={() => setShowReason('revoke')}>
                      Revoke
                    </button>
                  ) : null}
                  {f.status === 'PENDING_PAYMENT' ? (
                    <button className="btn-secondary" disabled={busy} onClick={withdraw}>
                      Withdraw
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label="Reason">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowReason(null)}>
                      Cancel
                    </button>
                    <button className={showReason === 'revoke' ? 'btn-danger' : 'btn-secondary'} disabled={busy || reason.trim().length < 3} onClick={confirmReasoned}>
                      {busy ? 'Submitting…' : `Confirm ${showReason}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function RegisterFisherModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [nin, setNin] = useState('');
  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [phone, setPhone] = useState('');
  const [homeDistrict, setHomeDistrict] = useState('Beau Vallon');
  const [homePort, setHomePort] = useState('Victoria Fishing Port');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m32/fishers', { nin, fullName, dob, phone: phone || undefined, homeDistrict, homePort });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register fisher" onClose={onClose}>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <Field label="NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} placeholder="SC900101012" />
        </Field>
        <Field label="Full name">
          <input className="field-input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>
        <Field label="Date of birth">
          <input className="field-input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
        </Field>
        <Field label="Phone (optional)">
          <input className="field-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Home district">
            <input className="field-input" value={homeDistrict} onChange={(e) => setHomeDistrict(e.target.value)} />
          </Field>
          <Field label="Home port">
            <input className="field-input" value={homePort} onChange={(e) => setHomePort(e.target.value)} />
          </Field>
        </div>
        <button className="btn-primary w-full" disabled={busy || !nin || !fullName || !dob} onClick={submit}>
          {busy ? 'Submitting…' : 'Submit registration (raises SeyPay fee intent)'}
        </button>
      </div>
    </Modal>
  );
}

// ── Licences ───────────────────────────────────────────────────────────────

function LicencesTab() {
  const { persona } = useAuth();
  const isLicensing = persona ? persona.roles.includes('SFA_LICENSING') : false;
  const isAnyOfficer = persona ? persona.roles.some((r) => LICENCE_LIST_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m32/licences?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<FishingLicence>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Licence no, vessel, operator…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['APPLIED', 'FEE_PENDING', 'ACTIVE', 'SUSPENDED', 'REVOKED', 'EXPIRED', 'REJECTED', 'WITHDRAWN'].map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
        {isLicensing ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New licence application
          </button>
        ) : null}
      </div>

      {!isAnyOfficer ? <RoleNotice roles={LICENCE_LIST_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No fishing licences match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Licence No</th>
                <th className="table-head">Class</th>
                <th className="table-head">Vessel</th>
                <th className="table-head">Zones</th>
                <th className="table-head">Valid to</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((l) => (
                <tr key={l.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(l.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{l.licenceNo}</td>
                  <td className="table-cell text-xs">{l.licenceClass.replaceAll('_', ' ')}</td>
                  <td className="table-cell">
                    {l.vesselName}
                    <div className="font-mono text-[11px] text-slate-400">{l.vesselRegNo}</div>
                  </td>
                  <td className="table-cell text-xs text-slate-500">{l.zoneCodes.join(', ')}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(l.validTo)}</td>
                  <td className="table-cell">
                    <StatusBadge status={l.status} />
                    {l.renewalReviewRequired ? <div className="mt-1 text-[10px] font-semibold text-amber-600">Renewal review needed</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <LicenceDetailModal
          id={selectedId}
          isLicensing={isLicensing}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
      {showCreate ? (
        <NewLicenceModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function LicenceDetailModal({ id, isLicensing, onClose, onChanged }: { id: string; isLicensing: boolean; onClose: () => void; onChanged: () => void }) {
  const lq = useApiQuery<FishingLicence>(`/api/v1/m32/licences/${id}`);
  const l = lq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDecision, setShowDecision] = useState<'review-approve' | 'review-reject' | 'renew-approve' | 'renew-reject' | 'suspend' | 'revoke' | null>(null);
  const [note, setNote] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const reinstate = () => call(() => api.post(`/api/v1/m32/licences/${id}/reinstate`));
  const withdraw = () => call(() => api.post(`/api/v1/m32/licences/${id}/withdraw`, { reason: note || undefined }));
  const triggerRenewal = () => call(() => api.post(`/api/v1/m32/licences/${id}/renew`));

  const confirm = () =>
    call(() => {
      if (showDecision === 'review-approve') return api.post(`/api/v1/m32/licences/${id}/review`, { approve: true, note: note || undefined });
      if (showDecision === 'review-reject') return api.post(`/api/v1/m32/licences/${id}/review`, { approve: false, rejectionReason: note });
      if (showDecision === 'renew-approve') return api.post(`/api/v1/m32/licences/${id}/renew/review`, { approve: true, note: note || undefined });
      if (showDecision === 'renew-reject') return api.post(`/api/v1/m32/licences/${id}/renew/review`, { approve: false, note: note || undefined });
      if (showDecision === 'suspend') return api.post(`/api/v1/m32/licences/${id}/suspend`, { reason: note });
      return api.post(`/api/v1/m32/licences/${id}/revoke`, { reason: note });
    });

  return (
    <Modal title={l ? `${l.licenceNo} — ${l.vesselName}` : 'Fishing licence'} onClose={onClose} wide>
      {lq.error ? <ErrorNote message={lq.error} /> : null}
      {lq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {l ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={l.status} />} />
            <Detail label="Class" value={l.licenceClass.replaceAll('_', ' ')} />
            <Detail label="Operator" value={`${l.operatorType}:${l.operatorId}`} mono />
            <Detail label="Vessel reg. no (M22)" value={l.vesselRegNo} mono />
            <Detail label="Vessel IMO" value={l.vesselImoNumber ?? '—'} mono />
            <Detail label="Gross tonnage" value={String(l.vesselGrossTonnage)} />
            <Detail label="Gear types" value={l.gearTypes.join(', ')} />
            <Detail label="Zones" value={l.zoneCodes.join(', ')} />
            <Detail label="Valid" value={`${formatDate(l.validFrom)} → ${formatDate(l.validTo)}`} />
          </div>
          {l.rejectionReason ? <ErrorNote message={`Rejected: ${l.rejectionReason}`} /> : null}
          {l.suspendedReason ? <ErrorNote message={`Suspended: ${l.suspendedReason}`} /> : null}
          {l.revokedReason ? <ErrorNote message={`Revoked: ${l.revokedReason}`} /> : null}

          {l.renewalRequestedAt ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-700">
              Renewal requested {formatDateTime(l.renewalRequestedAt)} — compliance rate{' '}
              {l.renewalComplianceRate !== null && l.renewalComplianceRate !== undefined ? `${(Number(l.renewalComplianceRate) * 100).toFixed(0)}%` : '—'}
              {l.renewalReviewRequired ? ' — below auto-renew threshold, officer review required.' : ' — auto-approved.'}
              {l.renewalRejectionReason ? <ErrorNote message={`Renewal rejected: ${l.renewalRejectionReason}`} /> : null}
            </div>
          ) : null}

          {isLicensing ? (
            <div className="border-t border-slate-100 pt-4">
              {!showDecision ? (
                <div className="flex flex-wrap gap-2">
                  {l.status === 'APPLIED' ? (
                    <>
                      <button className="btn-success" disabled={busy} onClick={() => setShowDecision('review-approve')}>
                        Approve application
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('review-reject')}>
                        Reject
                      </button>
                    </>
                  ) : null}
                  {l.status === 'ACTIVE' && l.renewalReviewRequired ? (
                    <>
                      <button className="btn-success" disabled={busy} onClick={() => setShowDecision('renew-approve')}>
                        Clear renewal (create fee intent)
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('renew-reject')}>
                        Reject renewal
                      </button>
                    </>
                  ) : null}
                  {l.status === 'ACTIVE' && !l.renewalRequestedAt ? (
                    <button className="btn-secondary" disabled={busy} onClick={triggerRenewal}>
                      Trigger renewal on operator's behalf
                    </button>
                  ) : null}
                  {l.status === 'ACTIVE' ? (
                    <button className="btn-secondary" disabled={busy} onClick={() => setShowDecision('suspend')}>
                      Suspend
                    </button>
                  ) : null}
                  {l.status === 'SUSPENDED' ? (
                    <button className="btn-success" disabled={busy} onClick={reinstate}>
                      Reinstate
                    </button>
                  ) : null}
                  {l.status === 'ACTIVE' || l.status === 'SUSPENDED' ? (
                    <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('revoke')}>
                      Revoke
                    </button>
                  ) : null}
                  {l.status === 'APPLIED' ? (
                    <button className="btn-secondary" disabled={busy} onClick={withdraw}>
                      Withdraw application
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label={showDecision.includes('reject') || showDecision === 'suspend' || showDecision === 'revoke' ? 'Reason' : 'Note (optional)'}>
                    <textarea className="field-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowDecision(null)}>
                      Cancel
                    </button>
                    <button className={showDecision.includes('approve') ? 'btn-success' : 'btn-danger'} disabled={busy} onClick={confirm}>
                      {busy ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewLicenceModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const zonesQ = useApiQuery<Page<FishingZone>>('/api/v1/m32/zones?limit=100&status=all');
  const [licenceClass, setLicenceClass] = useState<(typeof LICENCE_CLASSES)[number]>('ARTISANAL');
  const [operatorType, setOperatorType] = useState<'PERSON' | 'ENTITY'>('PERSON');
  const [operatorId, setOperatorId] = useState('');
  const [vesselRegNo, setVesselRegNo] = useState('');
  const [gearTypes, setGearTypes] = useState<string[]>([]);
  const [zoneCodes, setZoneCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (arr: string[], setArr: (v: string[]) => void, v: string) =>
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m32/licences', { licenceClass, operatorType, operatorId, vesselRegNo, gearTypes, zoneCodes });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New fishing licence application" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Licence class">
            <select className="field-input" value={licenceClass} onChange={(e) => setLicenceClass(e.target.value as (typeof LICENCE_CLASSES)[number])}>
              {LICENCE_CLASSES.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Operator type">
            <select className="field-input" value={operatorType} onChange={(e) => setOperatorType(e.target.value as 'PERSON' | 'ENTITY')}>
              <option value="PERSON">Person (NIN)</option>
              <option value="ENTITY">Entity (business ID)</option>
            </select>
          </Field>
        </div>
        <Field label={operatorType === 'PERSON' ? 'Operator NIN' : 'Operator business ID'}>
          <input className="field-input" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} />
        </Field>
        <Field label="Vessel registration no. (verified live against M22 Maritime & Ports)">
          <input className="field-input" value={vesselRegNo} onChange={(e) => setVesselRegNo(e.target.value)} placeholder="e.g. SC-FSH-0142" />
        </Field>
        <div>
          <div className="field-label">Gear types</div>
          <div className="flex flex-wrap gap-2">
            {GEAR_TYPES.map((g) => (
              <label key={g} className={`cursor-pointer rounded-lg border px-2.5 py-1 text-xs font-medium ${gearTypes.includes(g) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600'}`}>
                <input type="checkbox" className="mr-1.5" checked={gearTypes.includes(g)} onChange={() => toggle(gearTypes, setGearTypes, g)} />
                {g.replaceAll('_', ' ')}
              </label>
            ))}
          </div>
        </div>
        <div>
          <div className="field-label">Fishing zones</div>
          {zonesQ.loading ? (
            <Spinner label="Loading zones…" />
          ) : (
            <div className="flex flex-wrap gap-2">
              {(zonesQ.data?.items ?? []).map((z) => (
                <label key={z.code} className={`cursor-pointer rounded-lg border px-2.5 py-1 text-xs font-medium ${zoneCodes.includes(z.code) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600'}`}>
                  <input type="checkbox" className="mr-1.5" checked={zoneCodes.includes(z.code)} onChange={() => toggle(zoneCodes, setZoneCodes, z.code)} />
                  {z.name} ({z.code})
                </label>
              ))}
            </div>
          )}
        </div>
        <button
          className="btn-primary w-full"
          disabled={busy || !operatorId || !vesselRegNo || gearTypes.length === 0 || zoneCodes.length === 0}
          onClick={submit}
        >
          {busy ? 'Submitting…' : 'Submit application'}
        </button>
      </div>
    </Modal>
  );
}

// ── Zones (reference catalogue) ─────────────────────────────────────────────

function ZonesTab() {
  const { persona } = useAuth();
  const isLicensing = persona ? persona.roles.includes('SFA_LICENSING') : false;
  const [showCreate, setShowCreate] = useState(false);
  const zonesQ = useApiQuery<Page<FishingZone>>('/api/v1/m32/zones?limit=100&status=all');

  return (
    <div>
      <div className="mb-4 flex justify-end">
        {isLicensing ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New zone
          </button>
        ) : null}
      </div>
      {!isLicensing ? <RoleNotice roles={['SFA_LICENSING']} /> : null}
      {zonesQ.error ? <ErrorNote message={zonesQ.error} /> : null}
      <div className="card overflow-x-auto">
        {zonesQ.loading ? (
          <Spinner />
        ) : (zonesQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No fishing zones configured" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">Kind</th>
                <th className="table-head">Description</th>
                <th className="table-head">Active</th>
              </tr>
            </thead>
            <tbody>
              {zonesQ.data!.items.map((z) => (
                <tr key={z.id}>
                  <td className="table-cell font-mono text-xs font-medium">{z.code}</td>
                  <td className="table-cell">{z.name}</td>
                  <td className="table-cell text-xs">{z.kind.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{z.description ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={z.active ? 'ACTIVE' : 'CLOSED'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <CreateZoneModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            zonesQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateZoneModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<(typeof ZONE_KINDS)[number]>('INSHORE');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m32/zones', { code: code.toUpperCase(), name, kind, description: description || undefined });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New fishing zone" onClose={onClose}>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <Field label="Code (upper-kebab/underscore)">
          <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="MAHE_PLATEAU" />
        </Field>
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Kind">
          <select className="field-input" value={kind} onChange={(e) => setKind(e.target.value as (typeof ZONE_KINDS)[number])}>
            {ZONE_KINDS.map((k) => (
              <option key={k} value={k}>
                {k.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Description (optional)">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <button className="btn-primary w-full" disabled={busy || !code || !name} onClick={submit}>
          {busy ? 'Creating…' : 'Create zone'}
        </button>
      </div>
    </Modal>
  );
}
